<template>
  <div class="modal-body">
    <div class="register-form p-4">
      <!-- Success & Error Messages -->
      <div v-if="successMessage" class="alert alert-success text-center">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="alert alert-danger text-center">
        {{ errorMessage }}
      </div>

      <!-- Step Progress Bar -->
      <div class="mb-4">
        <div class="progress" style="height: 6px">
          <div
            class="progress-bar bg-pink"
            role="progressbar"
            :style="{ width: step * 33.33 + '%' }"
          ></div>
        </div>
        <div class="d-flex justify-content-between mt-2">
          <span :class="{ 'fw-bold text-pink': step === 1 }"
            ><i class="bi bi-person"></i> Personal Info</span
          >
          <span :class="{ 'fw-bold text-pink': step === 2 }"
            ><i class="bi bi-envelope"></i> Account Info</span
          >
          <span :class="{ 'fw-bold text-pink': step === 3 }"
            ><i class="bi bi-journal-bookmark"></i> Enrollment</span
          >
        </div>
      </div>

      <form @submit.prevent="updateStudent">
        <!-- Step 1 -->
        <div v-if="step === 1">
          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-person-fill"></i> First Name</label
            >
            <input
              type="text"
              class="form-control"
              v-model="form.first_name"
              required
            />
          </div>
          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-person-fill"></i> Last Name</label
            >
            <input
              type="text"
              class="form-control"
              v-model="form.last_name"
              required
            />
          </div>
          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-telephone-fill"></i> Phone</label
            >
            <input
              type="text"
              class="form-control"
              v-model="form.phone"
              required
            />
          </div>
          <div class="d-flex justify-content-end">
            <button
              type="button"
              class="btn btn-pink"
              :disabled="!stepValid"
              @click="nextStep"
            >
              Next <i class="bi bi-arrow-right"></i>
            </button>
          </div>
        </div>

        <!-- Step 2 -->
        <div v-if="step === 2">
          <div class="mb-3">
            <label class="form-label"
              ><i class="bi bi-envelope-fill"></i> Email</label
            >
            <input
              type="email"
              class="form-control"
              v-model="form.email"
              required
            />
          </div>

          <div class="mb-3">
            <label class="form-label">Password (leave blank to keep)</label>
            <div class="input-group">
              <input
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                v-model="form.password"
              />
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
              </button>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Confirm Password</label>
            <div class="input-group">
              <input
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-control"
                v-model="confirmPassword"
              />
              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="showConfirmPassword = !showConfirmPassword"
              >
                <i
                  :class="showConfirmPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"
                ></i>
              </button>
            </div>
            <small v-if="passwordMismatch" class="text-danger"
              >Passwords do not match</small
            >
          </div>

          <div class="d-flex justify-content-between">
            <button type="button" class="btn btn-secondary" @click="step = 1">
              <i class="bi bi-arrow-left"></i> Back
            </button>
            <button
              type="button"
              class="btn btn-pink"
              :disabled="!stepValid"
              @click="nextStep"
            >
              Next <i class="bi bi-arrow-right"></i>
            </button>
          </div>
        </div>

        <!-- Step 3 -->
        <!-- Step 3 -->
        <div v-if="step === 3">
          <!-- Grade -->
          <div class="row g-2 mb-3">
            <div class="col-md-3">
              <label class="form-label small">
                <i class="bi bi-mortarboard-fill"></i> Grade
              </label>
              <select
                class="form-select form-select-sm"
                v-model="selectedEnrollment.grade_id"
                @change="fetchSubjectsByGrade"
              >
                <option value="">Grade</option>
                <option
                  v-for="grade in grades"
                  :key="grade.id"
                  :value="grade.id"
                >
                  {{ grade.level }}
                </option>
              </select>
            </div>

            <div class="col-md-6">
              <label class="form-label small">
                <i class="bi bi-book-half"></i> Subject
              </label>
              <select
                class="form-select form-select-sm"
                v-model="selectedEnrollment.subject_id"
              >
                <option value="">Subject</option>
                <option
                  v-for="subject in subjects"
                  :key="subject.id"
                  :value="subject.id"
                >
                  {{ subject.name }}
                </option>
              </select>
            </div>

            <div class="col-md-3">
              <label class="form-label small">
                <i class="bi bi-check-circle"></i> Status
              </label>
              <select
                class="form-select form-select-sm"
                v-model="selectedEnrollment.status"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <!-- Add Button -->
          <button
            type="button"
            class="btn btn-success mb-3"
            @click="addEnrollment"
          >
            <i class="bi bi-plus-circle"></i> Add
          </button>

          <!-- Result Table -->
          <table
            class="table table-bordered table-striped"
            v-if="enrollments.length"
          >
            <thead class="table-dark">
              <tr>
                <th>Grade</th>
                <th>Subject</th>
                <th>Status</th>
                <th width="100">Action</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(row, index) in enrollments" :key="index">
                <td>{{ row.grade_name }}</td>

                <td>{{ row.subject_name }}</td>

                <td>
                  <span
                    class="badge"
                    :class="
                      row.status === 'active' ? 'bg-success' : 'bg-secondary'
                    "
                  >
                    {{ row.status }}
                  </span>
                </td>

                <td>
                  <button
                    class="btn btn-danger btn-sm"
                    @click="removeEnrollment(index)"
                  >
                    <i class="bi bi-trash"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="d-flex justify-content-between mt-3">
            <button type="button" class="btn btn-secondary" @click="step = 2">
              <i class="bi bi-arrow-left"></i> Back
            </button>

            <button
              type="submit"
              class="btn btn-pink"
              :disabled="!enrollments.length || loading"
            >
              <i class="bi bi-save"></i> {{ loading ? "Saving..." : "Save" }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "EditStudent",
  props: {
    student: { type: Object, required: true },
  },
  data() {
    return {
      step: 1,
      form: {
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        password: "",
        grade_id: "",
        subject_id: "",
        enrollment_status: "active",
      },
      enrollments: [],

      selectedEnrollment: {
        grade_id: "",
        subject_id: "",
        status: "active",
      },
      confirmPassword: "",
      showPassword: false,
      showConfirmPassword: false,
      loading: false,
      successMessage: "",
      errorMessage: "",
      grades: [],
      subjects: [],
    };
  },
  watch: {
    student: {
      immediate: true,
      handler(val) {
        if (val) {
          this.form = {
            first_name: val.first_name,
            last_name: val.last_name,
            phone: val.phone,
            email: val.email,
            password: "",
          };

          // Load existing enrollments
          if (val.enrollments) {
            this.enrollments = val.enrollments.map((e) => ({
              grade_id: e.grade_id,
              subject_id: e.subject_id,
              status: e.status,
              grade_name: e.grade?.level,
              subject_name: e.subject?.name,
            }));
          }
        }
      },
    },
  },
  computed: {
    passwordMismatch() {
      return (
        this.confirmPassword && this.form.password !== this.confirmPassword
      );
    },
    stepValid() {
      if (this.step === 1)
        return this.form.first_name && this.form.last_name && this.form.phone;
      if (this.step === 2) return this.form.email && !this.passwordMismatch;
      if (this.step === 3) return this.enrollments.length > 0;
      return false;
    },
  },
  mounted() {
    this.fetchGrades();
  },
  methods: {
    async fetchGrades() {
      try {
        const res = await axios.get("/a-get-grades");
        this.grades = res.data;
      } catch (err) {
        console.error(err);
      }
    },

    async fetchSubjectsByGrade() {
      this.subjects = [];

      if (!this.selectedEnrollment.grade_id) return;

      try {
        const res = await axios.get(
          `/a-grade-subjects?grade_id=${this.selectedEnrollment.grade_id}`,
        );

        this.subjects = res.data.subjects || [];
      } catch (err) {
        console.error(err);
      }
    },

    nextStep() {
      if (!this.stepValid) return;
      if (this.step < 3) this.step++;
      else this.updateStudent();
    },

    async updateStudent() {
      this.loading = true;
      this.successMessage = "";
      this.errorMessage = "";

      try {
        await axios.put(`/update-student/${this.student.id}`, {
          ...this.form,
          enrollments: this.enrollments.map((e) => ({
            grade_id: e.grade_id,
            subject_id: e.subject_id,
            status: e.status,
          })),
        });

        this.successMessage = "Student updated successfully!";
        this.$emit("student-edited");

        setTimeout(() => (this.successMessage = ""), 5000);
      } catch (err) {
        console.error(err);
        this.errorMessage =
          err.response?.data?.errors || "Something went wrong!";

        setTimeout(() => (this.errorMessage = ""), 5000);
      } finally {
        this.loading = false;
      }
    },

    addEnrollment() {
      if (
        !this.selectedEnrollment.grade_id ||
        !this.selectedEnrollment.subject_id
      )
        return;

      const exists = this.enrollments.some(
        (e) =>
          e.grade_id == this.selectedEnrollment.grade_id &&
          e.subject_id == this.selectedEnrollment.subject_id,
      );

      if (exists) {
        alert("Subject already added");
        return;
      }

      const grade = this.grades.find(
        (g) => g.id == this.selectedEnrollment.grade_id,
      );

      const subject = this.subjects.find(
        (s) => s.id == this.selectedEnrollment.subject_id,
      );

      this.enrollments.push({
        grade_id: this.selectedEnrollment.grade_id,
        subject_id: this.selectedEnrollment.subject_id,
        status: this.selectedEnrollment.status,
        grade_name: grade?.level,
        subject_name: subject?.name,
      });

      this.selectedEnrollment.subject_id = "";
      this.selectedEnrollment.status = "active";
    },

    removeEnrollment(index) {
      this.enrollments.splice(index, 1);
    },
  },
};
</script>

<style lang="scss" scoped>

.btn-pink {
  background-color: #e83e8c;
  color: #fff;
  border: none;
}
.btn-pink:hover {
  background-color: #d63384;
  color: #fff;
}

</style>